import Vue from "vue";
import structuralService from "../services/structural.service";
import { $handleError, $updateOrAdd, $deleteFromArray } from '@/utils';
import { ForgeStructural } from "@/scripts/js/forgeModels";

export const STRUCTURAL_VIEW = {
  namespaced: true,
  state: {
    searchText: "",
    entries: [],
    total: 0,
    loading: false,
    updateLoading: false,
    page: 1,
    itemsPerPage: 10,
    selectedItems: [],
    lastOpenedItemId: null,
    // AbortController for cancelling pending requests
    currentRequestController: null,
    sortingData: [
      {
        id: 'Name',
        title: 'Tag Name',
        property: 'Name',
        isSelected: true,
        isExpanded: false,
        isAsc: true,
      },
      {
        id: 'CreateDate',
        property: 'CreateDate',
        title: 'Date Added',
        isSelected: false,
        isExpanded: false,
        isAsc: true,
      },
    ],
  },
  getters: {
    GET_SEARCH_TEXT(state) {
      return state.searchText;
    },
    GET_ENTRIES(state) {
      return state.entries;
    },
    GET_TOTAL(state) {
      return state.total;
    },
    GET_LOADING(state) {
      return state.loading;
    },
    GET_UPDATE_LOADING(state) {
      return state.updateLoading;
    },
    GET_PAGE(state) {
      return state.page;
    },
    GET_ITEMS_PER_PAGE(state) {
      return state.itemsPerPage;
    },
    GET_PAGES(state) {
      return Math.ceil(state.total / state.itemsPerPage);
    },
    GET_SELECTED_ITEMS(state) {
      return state.selectedItems;
    },
    GET_LAST_OPENED_ITEM_ID(state) {
      return state.lastOpenedItemId;
    },
    GET_SORTING_DATA(state) {
      return state.sortingData;
    },
    GET_SORT_PROPERTY(state) {
      return state.sortingData.find((o) => o.isSelected).property;
    },
    GET_SORT_ORDER_ASC(state) {
      return state.sortingData.find((o) => o.isSelected).isAsc;
    },
    GET_CURRENT_REQUEST_CONTROLLER(state) {
      return state.currentRequestController;
    },
  },
  mutations: {
    SET_SEARCH_TEXT(state, val) {
      state.searchText = val;
    },
    SET_ENTRIES(state, val) {
      state.entries = val;
    },
    SET_TOTAL(state, val) {
      state.total = val;
    },
    SET_LOADING(state, val) {
      state.loading = val;
    },
    SET_UPDATE_LOADING(state, val) {
      state.updateLoading = val;
    },
    SET_PAGE(state, val) {
      state.page = val;
    },
    SET_ITEMS_PER_PAGE(state, val) {
      state.itemsPerPage = val;
    },
    SET_SELECTED_ITEMS(state, val) {
      state.selectedItems = val;
    },
    SET_LAST_OPENED_ITEM_ID(state, id) {
      state.lastOpenedItemId = id;
    },
    CLEAR_SELECTED_ITEMS(state) {
      state.selectedItems = [];
      state.entries.forEach(item => {
        if (item.isSelected) {
          item.isSelected = false;
        }
      });
    },
    UPDATE_SELECTED_ITEMS(state) {
      state.selectedItems = state.entries.filter(item => item.isSelected);
    },
    SET_SORTING_DATA(state, val) {
      state.sortingData = val;
    },
    SET_REQUEST_CONTROLLER(state, controller) {
      state.currentRequestController = controller;
    },
    CLEAR_REQUEST_CONTROLLER(state) {
      state.currentRequestController = null;
    },
    RESET_LIST_STATE(state) {
      state.entries = [];
      state.total = 0;
      state.page = 1;
      state.itemsPerPage = 10;
      state.selectedItems = [];
      state.lastOpenedItemId = null;
      state.searchText = "";
      state.sortingData = [
        {
          id: 'Name',
          title: 'Tag Name',
          property: 'Name',
          isSelected: true,
          isExpanded: false,
          isAsc: true,
        },
        {
          id: 'CreateDate',
          property: 'CreateDate',
          title: 'Date Added',
          isSelected: false,
          isExpanded: false,
          isAsc: true,
        },
      ];
    },
  },
  actions: {
    /**
     * Fetch structural tags with cancellation support
     */
    async GET({ commit, state }, { refresh, params }) {
      // Cancel any existing request before starting a new one
      if (state.currentRequestController) {
        state.currentRequestController.abort();
      }

      // Create new AbortController for this request
      const controller = new AbortController();
      commit('SET_REQUEST_CONTROLLER', controller);

      let options = {
        ...params
      }

      if (refresh) {
        commit('SET_ENTRIES', []);
        commit('SET_TOTAL', 0);
        options = {
          ...params,
          page: 1
        }
      }

      commit('SET_LOADING', true);

      try {
        const { data } = await structuralService.get(options, controller.signal);

        data.items = data.items.map(t => {
          t = Object.assign(new ForgeStructural(), t);
          t.parseContent();
          return t;
        });

        commit('SET_ENTRIES', data.items);
        commit('SET_TOTAL', data.total);
      } catch (err) {
        // Don't show error for cancelled requests
        if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
          $handleError(err);
        }
      } finally {
        commit('SET_LOADING', false);
        commit('CLEAR_REQUEST_CONTROLLER');
      }
    },

    /**
     * Get structural by ID with cancellation support
     */
    async GET_BY_ID({ rootState }, { id, signal = null }) {
      const { data } = await structuralService.getById(id, rootState.assetId, signal);
      let t = Object.assign(new ForgeStructural(), data);
      t.parseContent();
      return t;
    },
    UPDATE({ state, commit }, data) {
      commit('SET_UPDATE_LOADING', true);
      return structuralService
        .update(data)
        .then((res) => {
          $updateOrAdd(state.entries, res.data);
          Vue.prototype.$dialog.notify.success(
            `Structural Data Successfully ${data.id ? 'Updated' : 'Added'}.`,
          );
          if (!data.id) {
            commit('SET_TOTAL', ++state.total);
          }
        })
        .catch((err) => {
          $handleError(err);
        })
        .finally(() => {
          commit('SET_UPDATE_LOADING', false);
        });
    },
    DELETE({ state, commit }, id) {
      return structuralService
        .del(id)
        .then(() => {
          $deleteFromArray(state.entries, id);
          commit('SET_TOTAL', --state.total);
          Vue.prototype.$dialog.notify.success('Deleted Successfully', {
            position: 'top-right',
            timeout: 3000,
          });
        })
        .catch((err) => {
          $handleError(err);
        });
    },
    RESET_FOR_TAG_TYPE_ENTRY({ commit }) {
      commit('RESET_LIST_STATE');
    },
    UPDATE_SELECTION({ commit }) {
      commit('UPDATE_SELECTED_ITEMS');
    },
    CLEAR_SELECTION({ commit }) {
      commit('CLEAR_SELECTED_ITEMS');
    },
    /**
     * Cancel any pending request
     */
    CANCEL_CURRENT_REQUEST({ commit, state }) {
      if (state.currentRequestController) {
        state.currentRequestController.abort();
        commit('CLEAR_REQUEST_CONTROLLER');
        commit('SET_LOADING', false);
      }
    },
  }
}
